import React, { useState, useEffect } from 'react';
import { workWithUsAPI } from '../../../services/api';
import '../Shared/FormStyles.scss';

const ICON_OPTIONS = [
  { value: 'people', label: 'People / Team' },
  { value: 'growth', label: 'Growth / Chart' },
  { value: 'heart', label: 'Heart / Care' },
  { value: 'screen', label: 'Screen / Facilities' },
  { value: 'clock', label: 'Clock / Time' },
  { value: 'money', label: 'Money / Dollar' },
  { value: 'star', label: 'Star / Excellence' },
  { value: 'check', label: 'Check / Quality' },
  { value: 'building', label: 'Building' },
  { value: 'award', label: 'Award' },
];

const emptyValue = { icon: 'people', title: '', description: '', order: 0, isActive: true };

const WorkWithUsManager = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showValueForm, setShowValueForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [valueForm, setValueForm] = useState(emptyValue);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await workWithUsAPI.getContent();
      setContent(res.data);
    } catch {
      setError('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handlePageSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      await workWithUsAPI.updateContent(content);
      setSuccess('Page content saved successfully');
    } catch {
      setError('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const setField = (section, field, value) =>
    setContent(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  const openAdd = () => {
    setEditingIndex(null);
    setValueForm({ ...emptyValue, order: content?.values?.length || 0 });
    setShowValueForm(true);
  };

  const openEdit = (idx) => {
    setEditingIndex(idx);
    setValueForm({ ...content.values[idx] });
    setShowValueForm(true);
  };

  const cancelValueForm = () => {
    setShowValueForm(false);
    setEditingIndex(null);
    setValueForm(emptyValue);
  };

  const saveValue = () => {
    if (!valueForm.title.trim() || !valueForm.description.trim()) {
      setError('Title and description are required');
      return;
    }
    const updated = [...(content.values || [])];
    if (editingIndex !== null) {
      updated[editingIndex] = valueForm;
    } else {
      updated.push(valueForm);
    }
    setContent(prev => ({ ...prev, values: updated }));
    cancelValueForm();
    setError('');
  };

  const deleteValue = (idx) => {
    if (!window.confirm('Delete this value card?')) return;
    setContent(prev => ({ ...prev, values: prev.values.filter((_, i) => i !== idx) }));
  };

  const moveValue = (idx, dir) => {
    const updated = [...content.values];
    const swap = idx + dir;
    if (swap < 0 || swap >= updated.length) return;
    [updated[idx], updated[swap]] = [updated[swap], updated[idx]];
    setContent(prev => ({ ...prev, values: updated }));
  };

  if (loading) return <div>Loading...</div>;
  if (!content) return <div className="error-message">{error}</div>;

  return (
    <div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: 16 }}>{success}</div>}

      <form onSubmit={handlePageSave}>
        <div className="admin-form" style={{ marginBottom: 24 }}>
          <h2>Hero Section</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Label (small text above title)</label>
              <input type="text" value={content.hero?.label || ''} onChange={e => setField('hero', 'label', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={content.hero?.title || ''} onChange={e => setField('hero', 'title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Subtitle</label>
              <textarea rows={3} value={content.hero?.subtitle || ''} onChange={e => setField('hero', 'subtitle', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="admin-form" style={{ marginBottom: 24 }}>
          <h2>Values Section Header</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Label</label>
              <input type="text" value={content.valuesSection?.label || ''} onChange={e => setField('valuesSection', 'label', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={content.valuesSection?.title || ''} onChange={e => setField('valuesSection', 'title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Subtitle</label>
              <textarea rows={2} value={content.valuesSection?.subtitle || ''} onChange={e => setField('valuesSection', 'subtitle', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="admin-form" style={{ marginBottom: 24 }}>
          <h2>Bottom CTA</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={content.cta?.title || ''} onChange={e => setField('cta', 'title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={content.cta?.description || ''} onChange={e => setField('cta', 'description', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-actions" style={{ marginBottom: 32 }}>
          <button type="submit" className="dashboard-btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Page Content'}
          </button>
        </div>
      </form>

      {/* Value Cards */}
      <div className="admin-form" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Value Cards ({content.values?.length || 0})</h2>
          {!showValueForm && (
            <button type="button" className="dashboard-btn btn-primary" onClick={openAdd}>+ Add Card</button>
          )}
        </div>

        {showValueForm && (
          <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
            <h3 style={{ marginTop: 0 }}>{editingIndex !== null ? 'Edit Card' : 'New Card'}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Icon</label>
                <select value={valueForm.icon} onChange={e => setValueForm(f => ({ ...f, icon: e.target.value }))}>
                  {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input type="text" value={valueForm.title} onChange={e => setValueForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Collaborative Team" />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea rows={3} value={valueForm.description} onChange={e => setValueForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe this value..." />
              </div>
              <div className="form-group">
                <label>Order</label>
                <input type="number" value={valueForm.order} min="0" onChange={e => setValueForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="form-group">
                <label>
                  <input type="checkbox" checked={valueForm.isActive} onChange={e => setValueForm(f => ({ ...f, isActive: e.target.checked }))} />
                  {' '}Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="dashboard-btn btn-primary" onClick={saveValue}>Save Card</button>
              <button type="button" className="dashboard-btn btn-secondary" onClick={cancelValueForm}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="data-table">
        {(!content.values || content.values.length === 0) ? (
          <div className="empty-state">No value cards yet. Click "+ Add Card" above.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Move</th>
                <th>Icon</th>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {content.values.map((val, idx) => (
                <tr key={idx}>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn-edit" onClick={() => moveValue(idx, -1)} disabled={idx === 0} style={{ padding: '2px 8px' }}>↑</button>
                      <button className="btn-edit" onClick={() => moveValue(idx, 1)} disabled={idx === content.values.length - 1} style={{ padding: '2px 8px' }}>↓</button>
                    </div>
                  </td>
                  <td>{ICON_OPTIONS.find(o => o.value === val.icon)?.label || val.icon}</td>
                  <td>{val.title}</td>
                  <td style={{ maxWidth: 280 }}>{val.description?.substring(0, 80)}{val.description?.length > 80 ? '…' : ''}</td>
                  <td>{val.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => openEdit(idx)}>Edit</button>
                      <button className="btn-delete" onClick={() => deleteValue(idx)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default WorkWithUsManager;
